import {
  ExtranetCreateTicketCommentPostDto,
  ExtranetTicketStatusEnum,
  ExtranetUploadTicketAttachmentsDto,
} from '@pharmac-extranet/extranet-api'
import {
  ControlledInput,
  ExtranetContainer,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacButton,
  PharmacErrorToast,
  PharmacH6,
  PharmacIcon,
  PharmacLoadingDots,
  PharmacText,
  usePharmacToast,
} from '@pharmac/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChangeEvent, useRef, useState } from 'react'
import { Control, FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import { useCustomerContext } from '../../features/customer/CustomerProvider'
import { api } from '../../services/HttpService'
import { NewTicketAttachment } from './TicketAttachments'
import { TicketComment } from './TicketComment'
import { useTicket, useTicketQueryKeyFactory } from './useTicket'

type TicketCommentsProps = {
  ticketId: string
}

export const TicketComments = ({ ticketId }: TicketCommentsProps) => {
  const { t } = useLanguage()
  const { minDesktop } = useBreakpoint()
  const { ticket, comments, commentsIsLoading } = useTicket(ticketId)
  const { customer } = useCustomerContext()
  const { isPharmacCustomerService } = useAuthenticationContext()
  const toastHook = usePharmacToast()

  const [newAttachments, setNewAttachments] = useState<{ [id: string]: File }>({})

  const areNewAttachmentsSelected = Object.keys(newAttachments).length > 0

  const [isMutatingAttachments, setIsMutatingAttachments] = useState(false)
  const [isMutatingComments, setIsMutatingComments] = useState(false)

  const queryClient = useQueryClient()

  const formHook = useForm({ defaultValues: { comment: '' } })

  const { mutate: postComment } = useMutation({
    mutationFn: async (data: ExtranetCreateTicketCommentPostDto) => {
      setIsMutatingComments(true)
      await api.tickets.sendComment(customer!.id, ticketId, data)
    },
    onSettled: () => setIsMutatingComments(false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: useTicketQueryKeyFactory.getTicketComments(customer?.id, ticketId),
      })

      formHook.setValue('comment', '')
    },
  })

  const { mutate: uploadAttachments } = useMutation({
    mutationFn: async (dto: ExtranetUploadTicketAttachmentsDto) => {
      setIsMutatingAttachments(true)
      await api.tickets.postTicketAttachment(customer!.id, ticketId, dto, {
        transformRequest: [
          (data) => {
            if (data instanceof FormData) {
              if (data.has('files')) {
                data.delete('files')
              }

              for (const file of (dto.files || []) as File[]) {
                data.append('files[]', file as Blob, encodeURIComponent(file.name))
              }
            }

            return data
          },
        ],
      })
    },
    onSettled: () => setIsMutatingAttachments(false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: useTicketQueryKeyFactory.getTicketAttachments(customer!.id, ticketId),
      })

      setNewAttachments({})
    },
    onError: () => {
      toastHook.add({
        autoClose: true,
        autoCloseTimeout: 4000,
        toast: <PharmacErrorToast>{t('extranet:contact.wrongFileTypeError')}</PharmacErrorToast>,
      })
    },
  })

  const handleSubmit = (values: { comment: string }) => {
    if (values.comment && values.comment.length > 1) {
      postComment(values)
    }

    if (areNewAttachmentsSelected) {
      uploadAttachments({ files: Object.values(newAttachments) })
    }
  }

  const fileUploadRef = useRef<HTMLInputElement | null>(null)

  const handleFileInputClick = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.click()
    }
  }

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const t = event.currentTarget
    if (!t.files) return

    const newFiles: typeof newAttachments = {}

    for (const file of t.files) {
      newFiles[crypto.randomUUID()] = file
    }

    setNewAttachments((s) => ({ ...s, ...newFiles }))
  }

  const handleRemoveAttachment = (id: string) => {
    if (Object.keys(newAttachments).includes(id)) {
      setNewAttachments((s) => {
        const copy = { ...s }
        delete copy[id]

        return copy
      })
    }
  }

  const isLoading = isMutatingAttachments || isMutatingComments

  const isTicketClosed =
    ticket?.status === ExtranetTicketStatusEnum.Value07Closed ||
    ticket?.status === ExtranetTicketStatusEnum.Value06Resolved ||
    ticket?.status === ExtranetTicketStatusEnum.Value08CancelledByCustomer

  if (!comments || commentsIsLoading) {
    return null
  }

  return (
    <>
      <ExtranetContainer
        flexColumn
        full
        paddingLargeTop
      >
        <PharmacH6 paddingSmallBottom>{t('extranet:contact.comments')}</PharmacH6>
        {comments.length === 0 && <div>{t('extranet:contact.noComments')}</div>}
        {comments.length > 0 && (
          <ExtranetContainer
            full
            style={{
              paddingLeft: 'var(--spacing-medium)',
              overflow: 'hidden',
              border: '2px solid var(--color-accent-4)',
              borderRadius: 'var(--border-radius-small)',
            }}
            paddingSmallHorizontal={!minDesktop}
            flexColumnReverse
          >
            <ExtranetContainer
              full
              flexColumnReverse
              style={{
                overflowY: 'auto',
                overflowX: 'hidden',
                maxHeight: '400px',
                scrollbarGutter: 'stable',
                scrollbarWidth: 'thin',
              }}
              paddingSmallRight
              paddingMediumTop
              paddingMediumBottom
            >
              {comments.map((comment, i) => (
                <TicketComment
                  key={comment.author + i}
                  comment={comment}
                />
              ))}
            </ExtranetContainer>
          </ExtranetContainer>
        )}
      </ExtranetContainer>
      <ExtranetContainer
        flexColumn
        full
        paddingLargeTop
      >
        <FormProvider {...formHook}>
          <ExtranetContainer
            full
            flexRow
            paddingSmallBottom
          >
            <ExtranetContainer
              half
              justifyStart
            >
              <PharmacH6>{t('extranet:contact.addComment')}</PharmacH6>
            </ExtranetContainer>
            <ExtranetContainer
              half
              justifyEnd
            >
              <PharmacText>{formHook.watch('comment').length}/3500</PharmacText>
            </ExtranetContainer>
          </ExtranetContainer>

          <div style={{ width: '100%' }}>
            <ControlledInput
              disabled={isTicketClosed ? true : false || isPharmacCustomerService}
              type="textarea"
              name="comment"
              rules={{ maxLength: 3500 }}
              control={formHook.control as unknown as Control<FieldValues>}
            />
          </div>
          <ExtranetContainer
            full
            flexRow={minDesktop}
            flexColumn={!minDesktop}
            marginMediumTop
            alignCenter={!areNewAttachmentsSelected}
          >
            <ExtranetContainer
              flexRow
              half
            >
              {minDesktop &&
                Object.keys(newAttachments).length > 0 &&
                Object.entries(newAttachments).map(([id, file]) => (
                  <NewTicketAttachment
                    key={id}
                    attachment={file}
                    removeAttachment={() => handleRemoveAttachment(id)}
                  />
                ))}
            </ExtranetContainer>
            <ExtranetContainer
              full
              justifyEnd={minDesktop}
              spaceBetween={!minDesktop}
              alignCenter={minDesktop}
            >
              {isLoading && (
                <div style={{ marginRight: 'auto' }}>
                  <PharmacLoadingDots />
                </div>
              )}
              {minDesktop && (
                <PharmacText marginMediumRight>
                  {t('extranet:contact.acceptableFileTypes')}
                </PharmacText>
              )}
              <input
                ref={fileUploadRef}
                onChange={handleFileSelect}
                accept=".pdf,.png,.jpg,.webp"
                multiple
                type="file"
                hidden
              />

              <PharmacButton
                paddingMediumHorizontal
                size={minDesktop ? 'small' : 'medium'}
                variant="secondary"
                onClick={() => handleFileInputClick()}
                marginSmallRight
                disabled={isTicketClosed || areNewAttachmentsSelected || isPharmacCustomerService}
              >
                <PharmacIcon
                  icon="paperclip"
                  size="18px"
                />
                <PharmacText
                  marginSmallLeft
                  weight={isTicketClosed ? 'regular' : 'semibold'}
                >
                  {t('extranet:contact.addAttachment')}
                </PharmacText>
              </PharmacButton>
              <PharmacButton
                paddingMediumHorizontal
                size={minDesktop ? 'small' : 'medium'}
                disabled={
                  isLoading ||
                  isTicketClosed ||
                  (!formHook.formState.isDirty && !areNewAttachmentsSelected) ||
                  isPharmacCustomerService
                }
                variant={isTicketClosed ? 'secondary' : undefined}
                onClick={formHook.handleSubmit(handleSubmit)}
              >
                <PharmacText>{t('extranet:contact.sendButton')}</PharmacText>
              </PharmacButton>
            </ExtranetContainer>
            {!minDesktop && (
              <ExtranetContainer
                paddingMediumTop
                full
              >
                <PharmacText>{t('extranet:contact.acceptableFileTypes')}</PharmacText>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
          {!minDesktop && areNewAttachmentsSelected && (
            <ExtranetContainer
              paddingLargeTop
              flexRow
            >
              {Object.entries(newAttachments).map(([id, file]) => (
                <NewTicketAttachment
                  key={id}
                  attachment={file}
                  removeAttachment={() => handleRemoveAttachment(id)}
                />
              ))}
            </ExtranetContainer>
          )}
        </FormProvider>
      </ExtranetContainer>
    </>
  )
}

/*
              {comments.map((comment, i) => (
              <Fragment key={comment.date + i}>
                <ExtranetContainer
                  full
                  flexRow
                  style={{
                    border: '2px solid var(--color-accent-4)',
                    borderRadius: isAuthorComment(comment.author)
                      ? '15px 15px 0 15px'
                      : '0 15px 15px 15px',
                    backgroundColor: 'var(--color-accent-2)',
                  }}
                >
                  <ExtranetContainer
                    marginSmall
                    paddingSmallTop
                    alignCenter
                    style={{ width: minDesktop ? '5%' : '15%' }}
                    flexColumn
                  >
                    <ExtranetContainer
                      full
                      alignCenter
                      justifyCenter
                      style={{
                        backgroundColor: 'var(--color-green)',
                        borderRadius: '35px',
                        height: '40px',
                        width: '40px',
                      }}
                    >
                      <PharmacH6 color="white">
                        {parseInitials(parseExtranetAuthor(comment.author))}
                      </PharmacH6>
                    </ExtranetContainer>
                  </ExtranetContainer>
                  <ExtranetContainer
                    full
                    flexColumn
                    paddingMediumVertical
                  >
                    <ExtranetContainer flexRow>
                      <PharmacText weight="semibold">{comment.value}</PharmacText>
                    </ExtranetContainer>
                    <ExtranetContainer
                      full
                      flexRow
                      paddingSmallTop
                    >
                      <PharmacText>{parseExtranetAuthor(comment.author)}</PharmacText>
                      <PharmacText paddingMediumLeft>
                        {formatDateTimeString(comment.date)}
                      </PharmacText>
                    </ExtranetContainer>
                  </ExtranetContainer>
                </ExtranetContainer>
              </Fragment>
            ))}

  */
